"use client";

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import type { Message } from "@/lib/core/crm/types/message";
import {
  useMessageAttachments,
  useMessageConnectionId,
} from "@/lib/hooks/messages/useMessageAttachments";
import { useContactByEmail } from "@/features/contacts/hooks";
import { useSaveAttachmentsFolderBrowse } from "./useSaveAttachmentsFolderBrowse";
import { type Attachment, type UploadResults } from "./SaveAttachmentsToGoogleDriveModal.types";

/**
 * "Save attachments to Google Drive" — destination state.
 *
 * ## What changed, and why it matters
 *
 * This used to make the operator BROWSE the customer root and pick a folder by
 * eye, every time, with no default. That is the read-side name-matching
 * `COMPANY_DRIVE_FOLDER_IDENTITY` forbids: the folder that looks right and the
 * folder bound to `companies.google_drive_folder_id` are not reliably the same
 * one — `Fedex`/`FedEx`-shaped duplicates and merge orphans both sit under that
 * root looking perfectly plausible.
 *
 * Now the default destination is resolved from the CRM: the sender's email ->
 * contact -> company -> that company's own folder, server-side, by id. The
 * operator confirms rather than hunts.
 *
 * Browsing is still here, because a default is a default and not a cage —
 * `destinationMode: "folder"` reveals the same picker and passes an explicit
 * `folderId`. The server bounds that to the customer-folders subtree (it writes
 * as a delegated identity, so an unchecked id would be a write-anywhere
 * primitive); this hook does not need to know the rule, only that a refusal
 * comes back as an error like any other.
 *
 * The upload itself no longer depends on the operator having Google Drive
 * connected — Hub writes as the service identity — so Drive status is now only
 * consulted for the BROWSE path, which genuinely needs the person's own access
 * to list folders.
 */

interface UseSaveAttachmentsOptions {
  message: Message;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

/** Which destination the operator is aiming at. */
export type DestinationMode = "company" | "folder";

export function useSaveAttachmentsToGoogleDriveState({
  message,
  onOpenChange,
  open,
}: UseSaveAttachmentsOptions) {
  const { session } = useUniversalAuth();
  const [destinationMode, setDestinationMode] = useState<DestinationMode>("company");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(null);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());

  // The route authorizes the MAILBOX, not the message: it needs the connection
  // id so `resolveConnectionAccess` can confirm the caller owns (or was shared)
  // the account the attachments live in. See `useMessageConnectionId`.
  const connectionId = useMessageConnectionId(message);

  // The default destination. The sender is the right address to resolve from:
  // this is inbound customer mail, so the counterparty is who the documents
  // belong to. A message from someone with no CRM contact, or a contact with no
  // company, simply has no default — the modal then asks the operator to browse
  // rather than guessing at a company.
  const senderEmail = message?.sender?.email ?? "";
  const { data: senderContact, isLoading: contactLoading } = useContactByEmail(
    open ? senderEmail : "",
  );
  const companyId: string | null = senderContact?.company_id ?? null;
  const companyName: string | null =
    (senderContact?.companies as { name?: string } | null | undefined)?.name ?? null;

  const {
    data: attachments = [],
    isLoading: attachmentsLoading,
    error: attachmentsError,
  } = useMessageAttachments(message, open);

  // Browsing is its own concern with its own dependency — the operator's OWN
  // Drive access — and it fetches nothing until the picker is actually open. The
  // company default needs none of it, which is what lets a WTC admin with no
  // Google identity (most of them, per GOOGLE_DRIVE_ACCESS_DELEGATION) use it.
  const browsing = open && destinationMode === "folder";
  const browse = useSaveAttachmentsFolderBrowse({ enabled: browsing });
  const { selectedClient } = browse;

  // With no company to default to there is nothing to confirm, so open straight
  // into the picker instead of showing an empty destination the operator cannot
  // act on (ERROR_STATES_ACTIONABLE).
  useEffect(() => {
    if (open && !contactLoading && !companyId) {
      setDestinationMode("folder");
    }
  }, [open, contactLoading, companyId]);

  /** Is there a destination the operator could actually save to right now? */
  const hasDestination =
    destinationMode === "company" ? Boolean(companyId) : Boolean(selectedClient);

  const handleUpload = useCallback(async () => {
    if (!hasDestination) {
      toast.error(
        destinationMode === "company"
          ? "No company to save to — pick a folder instead"
          : "Please select a folder",
      );
      return;
    }
    if (!attachments || attachments.length === 0) {
      toast.error("No attachments found in this message");
      return;
    }
    if (selectedAttachmentIds.size === 0) {
      toast.error("Please select at least one attachment to save");
      return;
    }
    if (!session?.access_token) {
      toast.error("No authentication token found");
      return;
    }
    if (!connectionId) {
      toast.error("Couldn't work out which mailbox this message is in");
      return;
    }

    setIsUploading(true);
    setUploadResults(null);

    try {
      const response = await fetch("/api/drive/share-files", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          companyId,
          // Present only on the override. Absent means "this company's folder",
          // which the server resolves from the companies row.
          ...(destinationMode === "folder" && selectedClient
            ? { folderId: selectedClient.id }
            : {}),
          crmEmail: {
            connectionId,
            messageId: message.providerMessageId,
            attachmentIds: Array.from(selectedAttachmentIds),
          },
          source: { kind: "Email", label: message.subject },
        }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        const detail: string[] = Array.isArray(payload?.details) ? payload.details : [];
        const message_ =
          payload?.error || `Couldn't save these attachments (${response.statusText})`;
        setUploadResults({
          uploaded: [],
          errors: (detail.length > 0 ? detail : [message_]).map((error: string) => ({
            filename: "Save",
            error,
          })),
        });
        toast.error(message_);
        return;
      }

      const filed: Array<{
        filename: string;
        id: string;
        webViewLink: string | null;
        skipped: boolean;
      }> = payload?.filed ?? [];
      const errors: string[] = payload?.errors ?? [];

      setUploadResults({
        uploaded: filed.map((f) => ({
          filename: f.filename,
          id: f.id,
          webViewLink: f.webViewLink ?? "",
        })),
        errors: errors.map((error) => ({ filename: "Attachment", error })),
      });

      const destinationLabel =
        destinationMode === "company" ? (companyName ?? "the company folder") : selectedClient!.name;
      const freshlySaved = filed.filter((f) => !f.skipped).length;
      const alreadyThere = filed.length - freshlySaved;

      if (freshlySaved > 0) {
        toast.success(`Saved ${freshlySaved} attachment(s) to "${destinationLabel}"`);
      }
      // An already-present file is not a failure and not a new upload — saying
      // "saved" would be a lie and saying nothing looks like it did nothing.
      if (alreadyThere > 0 && freshlySaved === 0) {
        toast.info(`Already in "${destinationLabel}" — nothing to save`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} attachment(s) couldn't be saved. See details below.`);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Something went wrong saving to Drive";
      toast.error(`Failed to save attachments: ${errorMessage}`);
      setUploadResults({
        uploaded: [],
        errors: [{ filename: "Save", error: errorMessage }],
      });
    } finally {
      setIsUploading(false);
    }
  }, [
    hasDestination,
    destinationMode,
    attachments,
    selectedAttachmentIds,
    session,
    message,
    connectionId,
    companyId,
    companyName,
    selectedClient,
  ]);

  const handleClose = useCallback(() => {
    setDestinationMode("company");
    browse.resetBrowse();
    setUploadResults(null);
    setSelectedAttachmentIds(new Set());
    setIsUploading(false);
    onOpenChange(false);
  }, [browse, onOpenChange]);

  const toggleAttachmentSelection = useCallback((attachmentId: string) => {
    setSelectedAttachmentIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(attachmentId)) {
        newSelected.delete(attachmentId);
      } else {
        newSelected.add(attachmentId);
      }
      return newSelected;
    });
  }, []);

  const selectAllAttachments = useCallback(() => {
    if (attachments && attachments.length > 0) {
      setSelectedAttachmentIds(new Set(attachments.map((att: Attachment) => att.id)));
    }
  }, [attachments]);

  const deselectAllAttachments = useCallback(() => {
    setSelectedAttachmentIds(new Set());
  }, []);

  return {
    attachments,
    attachmentsError,
    ...browse,
    companyId,
    companyName,
    deselectAllAttachments,
    destinationMode,
    handleClose,
    handleUpload,
    hasDestination,
    isLoading: contactLoading || attachmentsLoading || (browsing && browse.listingLoading),
    isUploading,
    selectAllAttachments,
    selectedAttachmentIds,
    setDestinationMode,
    toggleAttachmentSelection,
    uploadResults,
  };
}
